import React, { useMemo, useState } from 'react';
import { useWidgetBuilder } from '../context/WidgetBuilderContext';
import { useBuilderLabels } from '../context/BuilderLocaleContext';
import { generateEmbedSnippets } from '../utils/generateEmbedSnippet';
import { getRuntimeOrigin } from '../utils/runtimeOrigin';

export default function EmbedPanel({ className = '' }) {
  const t = useBuilderLabels();
  const { config, widgetId } = useWidgetBuilder();
  const [tab, setTab] = useState('iframe');
  const [copied, setCopied] = useState(false);

  const snippets = useMemo(
    () => generateEmbedSnippets(widgetId, config),
    [widgetId, config]
  );
  const code = tab === 'iframe' ? snippets.iframe : snippets.script;
  const liveEmbedUrl = `${getRuntimeOrigin()}/w/${widgetId}?theme=${config.theme}`;
  const embedTestUrl = `/embed-test.html?id=${encodeURIComponent(widgetId)}&theme=${encodeURIComponent(config.theme)}`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <aside className={`bg-white border-l border-slate-200 flex flex-col ${className}`}>
      <div className="p-5 border-b border-slate-200">
        <h2 className="text-lg font-bold text-slate-900">{t('embedCode')}</h2>
        <p className="text-sm text-slate-500">{t('embedIntro')}</p>
      </div>

      <div className="p-5 flex-1 flex flex-col gap-4 overflow-y-auto min-h-0">
        <div className="flex rounded-lg border border-slate-200 p-1 text-sm">
          {['iframe', 'script'].map((tabId) => (
            <button
              key={tabId}
              type="button"
              onClick={() => setTab(tabId)}
              className={`flex-1 py-1.5 rounded-md font-semibold capitalize transition-colors ${
                tab === tabId ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {tabId === 'iframe' ? t('tabIframe') : t('tabScript')}
            </button>
          ))}
        </div>

        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-lg p-3">
          <p>{t('embedThemeNote', { theme: config.theme })}</p>
        </div>

        <pre className="flex-1 text-xs p-3 rounded-lg overflow-auto bg-slate-900 text-slate-100 font-mono leading-relaxed">
          {code}
        </pre>

        <button
          type="button"
          onClick={handleCopy}
          className="w-full py-3 rounded-lg font-bold text-sm text-white bg-brand-600 hover:bg-brand-700 transition-colors"
        >
          {copied ? t('copied') : t('copyEmbed')}
        </button>

        <a
          href={liveEmbedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full py-2.5 text-center rounded-lg text-sm font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
        >
          {t('openLiveEmbed')}
        </a>
        <a
          href={embedTestUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full py-2.5 text-center rounded-lg text-sm font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
        >
          {t('openEmbedTest')}
        </a>
        {import.meta.env.DEV && (
          <p className="text-[10px] text-slate-400 text-center leading-relaxed">{t('embedDevNote')}</p>
        )}

        <p className="text-[10px] text-slate-400 text-center">
          {t('embedWidgetId')}: <code className="font-mono">{widgetId}</code>
        </p>
      </div>
    </aside>
  );
}
